import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVirtualizer } from "@tanstack/react-virtual";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import type { PhotoResponse } from "@egalregal/shared";
import { useOpenContainer } from "../../shared/store/useOpenContainer";
import { readableContainers, useAppStore } from "../../shared/store/app";
import { getMirroredPlacements } from "../search/catalogStore";
import { useT } from "../../shared/i18n";
import {
  PhotoCard,
  type PhotoCardAction,
  type PhotoCardRelated,
} from "../../shared/components/PhotoCard";
import { useScrollArea } from "../shell/scrollArea";
import { GalleryLightbox } from "./GalleryLightbox";
import { photoKey, photoThumbnail } from "./photoUrls";

/** Photos per row below the `sm` breakpoint (matches the old static grid). */
const NARROW_COLUMN_COUNT = 3;
/** Photos per row at `sm` and up (matches the old static grid). */
const WIDE_COLUMN_COUNT = 4;
/** Row height guess until the virtualizer measures the real one. */
const ESTIMATED_ROW_HEIGHT = 320;
/**
 * Inter-card gutter in MUI spacing units (2 = 16px), applied on both axes:
 * the container's `columnSpacing` pads each tile horizontally and the tile's
 * own bottom padding creates the vertical gap.
 */
const TILE_GUTTER_SPACING = 2;

/**
 * The gallery photo grid + lightbox, shared by the browse and appearance modes.
 * Rows are virtualized against the shell's content scroll element (spec §2 —
 * the window no longer scrolls) and chunked by the responsive column count;
 * tile images load lazily so off-screen previews are never fetched. Each tile
 * opens the normal lightbox, whose footer navigates to the container or item
 * behind the photo — identical navigation in both modes. The lightbox always
 * sees the full photo list, so prev/next order is unaffected by row chunking.
 *
 * @param photos the full photo list to grid (and to navigate in the lightbox).
 */
export function PhotoGrid({ photos }: { photos: PhotoResponse[] }) {
  const t = useT();
  const navigate = useNavigate();
  const openContainer = useOpenContainer();
  const pool = useAppStore(readableContainers);
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  // Item photos identify their host container in the card sheet; a photo's own
  // or the item's UUID means nothing to the user (only the box has a label).
  const hostIdentity = useMemo(() => {
    const byId = new Map(pool.map((container) => [container.id, container]));
    const map = new Map<string, { uuid: string; name: string }>();
    for (const placement of getMirroredPlacements()) {
      if (map.has(placement.itemId)) continue;
      const container = byId.get(placement.containerId);
      if (container) map.set(placement.itemId, { uuid: container.id, name: container.name });
    }
    return map;
  }, [pool, photos]);

  const identityOf = (photo: PhotoResponse) =>
    photo.containerId
      ? {
          uuid: photo.containerId,
          name: photo.containerName ?? photo.file?.name ?? "",
        }
      : photo.itemId
        ? hostIdentity.get(photo.itemId)
        : undefined;

  const actionsOf = (photo: PhotoResponse): PhotoCardAction[] => {
    const containerId = photo.containerId;
    const itemId = photo.itemId;
    return [
      ...(containerId
        ? [
            {
              key: "container",
              label: t("goToContainer"),
              onClick: () => void openContainer(containerId),
            },
          ]
        : []),
      ...(itemId
        ? [
            {
              key: "item",
              label: t("goToItem"),
              onClick: () => navigate(`/items/${itemId}`),
            },
          ]
        : []),
    ];
  };

  // Every related entry carries the full panel data for its photo: selecting it
  // swaps the header, subtitle, identity, actions and lightbox target.
  const panelEntryOf = (photo: PhotoResponse, index: number): PhotoCardRelated => {
    const isItemPhoto = Boolean(photo.itemId ?? photo.itemName);
    return {
      key: photoKey(photo),
      image: photoThumbnail(photo),
      alt: photo.itemName ?? photo.file?.name ?? "",
      title: isItemPhoto
        ? (photo.itemName ?? "")
        : (photo.file?.name ?? photo.containerName ?? ""),
      secondary: isItemPhoto ? undefined : photo.containerPath,
      typeLabel:
        photo.itemPhotoType === "nutrition" ? t("galleryFilterNutrition") : undefined,
      identity: identityOf(photo),
      actions: actionsOf(photo),
      onOpen: () => setLightboxIdx(index),
    };
  };

  const relatedOf = (photo: PhotoResponse): PhotoCardRelated[] =>
    photos
      .map((sibling, index) => ({ sibling, index }))
      .filter(
        ({ sibling }) =>
          photoKey(sibling) !== photoKey(photo) &&
          (photo.containerId
            ? sibling.containerId === photo.containerId
            : photo.itemId
              ? sibling.itemId === photo.itemId
              : false),
      )
      .slice(0, 32)
      .map(({ sibling, index }) => panelEntryOf(sibling, index));

  const columnCount = useGalleryColumnCount();
  const rows = useMemo(() => chunkRows(photos, columnCount), [photos, columnCount]);
  const scrollArea = useScrollArea();
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  // The list sits below the gallery header/filters inside the content scroll
  // element, so its offset is the virtualizer's scroll margin. Re-measure on
  // photo/column changes and on resize (the header may wrap).
  const measureMargin = useCallback(() => {
    const scroll = scrollArea?.current ?? (typeof document !== "undefined" ? document.documentElement : null);
    const grid = gridRef.current;
    if (!scroll || !grid) {
      setScrollMargin(0);
      return;
    }
    const margin = grid.getBoundingClientRect().top - scroll.getBoundingClientRect().top + scroll.scrollTop;
    setScrollMargin(Math.max(0, Math.round(margin)));
  }, [scrollArea]);

  useLayoutEffect(() => {
    measureMargin();
  }, [measureMargin, rows.length, columnCount]);

  useEffect(() => {
    window.addEventListener("resize", measureMargin);
    return () => window.removeEventListener("resize", measureMargin);
  }, [measureMargin]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollArea?.current ?? (typeof document !== "undefined" ? document.documentElement : null),
    estimateSize: () => ESTIMATED_ROW_HEIGHT,
    overscan: 2,
    scrollMargin,
  });
  const virtualRows = virtualizer.getVirtualItems();

  return (
    <>
      <Box
        ref={gridRef}
        data-testid="gallery-grid"
        sx={{ position: "relative", height: virtualizer.getTotalSize() }}
      >
        {virtualRows.map((virtualRow) => (
          <Box
            key={virtualRow.key}
            data-testid="gallery-row"
            data-index={virtualRow.index}
            ref={virtualizer.measureElement}
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              transform: `translateY(${virtualRow.start - scrollMargin}px)`,
            }}
          >
            {/* Gutter rules (ADR-0031): this row wrapper is the virtualizer's
              measured box, so it carries no padding or margin of its own.
              Row spacing stays 0 — a spaced grid container pulls its content
              upward via a negative top margin, which would escape the
              measured box and clip the previous row's caption. The vertical
              gutter lives on each tile's bottom padding, inside the measured
              box; the horizontal gutter comes from the container's
              `columnSpacing`, which only pads the items (no vertical
              margin). */}
            <Grid container rowSpacing={0} columnSpacing={TILE_GUTTER_SPACING} data-testid="gallery-row-grid">
              {rows[virtualRow.index]!.map((photo, i) => (
                <Grid
                  key={photoKey(photo)}
                  item
                  xs={12 / columnCount}
                  data-testid="gallery-photo"
                  sx={{ pb: TILE_GUTTER_SPACING }}
                >
                  <PhotoTile
                    photo={photo}
                    index={virtualRow.index * columnCount + i}
                    identity={
                      photo.containerId
                        ? {
                            uuid: photo.containerId,
                            name: photo.containerName ?? photo.file?.name ?? "",
                          }
                        : photo.itemId
                          ? hostIdentity.get(photo.itemId)
                          : undefined
                    }
                    related={relatedOf(photo)}
                    onOpen={setLightboxIdx}
                    onOpenContainer={() => {
                      if (photo.containerId) void openContainer(photo.containerId);
                    }}
                    onOpenItem={() => {
                      if (photo.itemId) navigate(`/items/${photo.itemId}`);
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}
      </Box>

      <GalleryLightbox
        open={lightboxIdx !== null}
        photos={photos}
        index={lightboxIdx}
        onClose={() => setLightboxIdx(null)}
        onIndexChange={setLightboxIdx}
        onOpenContainer={(containerId) => {
          setLightboxIdx(null);
          void openContainer(containerId);
        }}
        onOpenItem={(itemId) => {
          setLightboxIdx(null);
          navigate(`/items/${itemId}`);
        }}
      />
    </>
  );
}

/** Groups photos into rows of `columnCount` (last row may be partial). */
function chunkRows(photos: PhotoResponse[], columnCount: number): PhotoResponse[][] {
  const rows: PhotoResponse[][] = [];
  for (let i = 0; i < photos.length; i += columnCount) {
    rows.push(photos.slice(i, i + columnCount));
  }
  return rows;
}

/** Responsive column count, following the MUI breakpoints (ADR-0031). */
function useGalleryColumnCount(): number {
  const theme = useTheme();
  const smUp = useMediaQuery(theme.breakpoints.up("sm"));
  return smUp ? WIDE_COLUMN_COUNT : NARROW_COLUMN_COUNT;
}

interface PhotoTileProps {
  photo: PhotoResponse;
  /** Index into the full photo list (lightbox navigates the whole list). */
  index: number;
  /** Container identity for the sheet; never an item or photo UUID. */
  identity?: { uuid: string; name: string };
  related: readonly PhotoCardRelated[];
  onOpen: (index: number) => void;
  onOpenContainer: () => void;
  onOpenItem: () => void;
}

/** One gallery tile: the shared photo card, fit media and two caption lines. */
function PhotoTile({
  photo,
  index,
  identity,
  related,
  onOpen,
  onOpenContainer,
  onOpenItem,
}: PhotoTileProps) {
  const t = useT();
  const isItemPhoto = Boolean(photo.itemId ?? photo.itemName);
  const title = isItemPhoto
    ? (photo.itemName ?? "")
    : (photo.containerName ?? photo.file?.name ?? "");
  // The panel names the photo itself (file name, or the item name for item
  // photos); the caption keeps naming the container.
  const panelTitle = isItemPhoto
    ? (photo.itemName ?? "")
    : (photo.file?.name ?? photo.containerName ?? "");
  return (
    <PhotoCard
      image={photoThumbnail(photo)}
      imageAlt={photo.itemName ?? photo.file?.name ?? ""}
      title={title}
      secondary={isItemPhoto ? undefined : photo.containerPath}
      panelTitle={panelTitle}
      panelSecondary={isItemPhoto ? undefined : photo.containerPath}
      typeLabel={photo.itemPhotoType === "nutrition" ? t("galleryFilterNutrition") : undefined}
      identity={identity}
      related={related}
      onOpen={() => onOpen(index)}
      actions={[
        ...(photo.containerId
          ? [{ key: "container", label: t("goToContainer"), onClick: onOpenContainer }]
          : []),
        ...(photo.itemId
          ? [{ key: "item", label: t("goToItem"), onClick: onOpenItem }]
          : []),
      ]}
      cardTestId="gallery-card"
      titleTestId="gallery-photo-name"
      secondaryTestId="gallery-photo-path"
    />
  );
}
